import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useQuickStatus, type RestaurantStatus } from '@/features/opening-times/hooks/useQuickStatus'
import { cn } from '@/lib/utils'

interface RestaurantStatusToggleProps {
  restaurantId: number
  className?: string
}

type ToggleTarget = 'closed' | 'delivery_only' | 'open'

/**
 * Inline 3-way restaurant status toggle.
 *
 * Mirrors the old RestaurantApp's MaterialButtonToggleGroup on the orders screen:
 *   [ Geschlossen ] [ Kein Lieferdienst ] [ Geöffnet ]
 *
 * State transitions are routed through `setToggleStatus` which atomically
 * removes all currently-active closures and (if needed) adds a new one,
 * matching the legacy app's behavior. Without that, switching directly from
 * "Geschlossen" to "Kein Lieferdienst" would stack two closures and the UI
 * would appear to ignore the second click.
 *
 * Real-time sync: a parent NotificationProvider subscribes to opening_times
 * SSE events and invalidates `restaurant-status`, so changes from another
 * user/tab refresh this component automatically.
 */
export function RestaurantStatusToggle({ restaurantId, className }: RestaurantStatusToggleProps) {
  const { t } = useTranslation(['quickStatus', 'orders'])
  const {
    status,
    isLoading,
    canOpenNow,
    closedReason,
    setToggleStatus,
    isClosing,
    isOpening,
  } = useQuickStatus(restaurantId)

  const [confirming, setConfirming] = useState<Exclude<ToggleTarget, 'open'> | null>(null)
  const [optimisticTarget, setOptimisticTarget] = useState<ToggleTarget | null>(null)
  const isPending = isClosing || isOpening || optimisticTarget !== null

  const effectiveStatus: RestaurantStatus = optimisticTarget ?? status

  const handleSelect = (target: ToggleTarget) => {
    if (isPending || isLoading) return
    if (target === effectiveStatus) return
    if (target === 'open') {
      void runAction('open')
    } else {
      setConfirming(target)
    }
  }

  const runAction = async (target: ToggleTarget) => {
    if (isPending) return
    if (target === 'open' && !canOpenNow && status === 'closed' && closedReason === 'regular_schedule') {
      toast.info(t('quickStatus:label.closedByRegularSchedule'))
      setConfirming(null)
      return
    }
    setOptimisticTarget(target)
    setConfirming(null)
    try {
      const comment =
        target === 'delivery_only' || target === 'closed'
          ? t('quickStatus:comments.temporarilyClosed')
          : undefined
      const ran = await setToggleStatus(target, comment)
      if (!ran) return // Mutex blocked — another op was already in flight
      if (target === 'open') {
        toast.success(t('quickStatus:toast.restaurantOpened'))
      } else if (target === 'delivery_only') {
        toast.success(t('quickStatus:toast.deliveryOnlyEnabled'))
      } else {
        toast.success(t('quickStatus:toast.restaurantClosed'))
      }
    } catch {
      toast.error(t('quickStatus:toast.error'))
    } finally {
      setOptimisticTarget(null)
    }
  }

  const buttonsDisabled = isPending || isLoading

  return (
    <div className={cn('w-full', className)}>
      <div
        role="radiogroup"
        aria-label={t('quickStatus:label.restaurantStatus')}
        title={t('orders:statusToggle.description')}
        className="grid grid-cols-3 gap-0 rounded-lg border border-border/80 bg-card overflow-hidden shadow-sm shadow-black/[0.03]"
      >
        <ToggleButton
          label={t('orders:statusToggle.closed')}
          isActive={effectiveStatus === 'closed'}
          isLoading={isLoading || optimisticTarget === 'closed'}
          activeClasses="bg-status-error text-white"
          onClick={() => handleSelect('closed')}
          disabled={buttonsDisabled}
          showActiveReason={status === 'closed' && closedReason === 'regular_schedule'}
          activeReason={t('quickStatus:label.closedByRegularSchedule')}
        />
        <ToggleButton
          label={t('orders:statusToggle.deliveryOnly')}
          isActive={effectiveStatus === 'delivery_only'}
          isLoading={isLoading || optimisticTarget === 'delivery_only'}
          activeClasses="bg-status-warning text-white"
          onClick={() => handleSelect('delivery_only')}
          disabled={buttonsDisabled}
          className="border-x"
        />
        <ToggleButton
          label={t('orders:statusToggle.open')}
          isActive={effectiveStatus === 'open'}
          isLoading={isLoading || optimisticTarget === 'open'}
          activeClasses="bg-status-success text-white"
          onClick={() => handleSelect('open')}
          disabled={buttonsDisabled}
        />
      </div>

      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'delivery_only'
                ? t('quickStatus:confirm.deliveryOnlyTitle')
                : t('quickStatus:confirm.closeTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'delivery_only'
                ? t('quickStatus:confirm.deliveryOnlyIndefiniteDescription')
                : t('quickStatus:confirm.closeIndefiniteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quickStatus:confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirming && void runAction(confirming)}
              className={cn(
                confirming === 'delivery_only'
                  ? 'bg-status-warning hover:bg-status-warning/90'
                  : 'bg-status-error hover:bg-status-error/90'
              )}
            >
              {confirming === 'delivery_only'
                ? t('quickStatus:confirm.enableDeliveryOnly')
                : t('quickStatus:confirm.closeRestaurant')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface ToggleButtonProps {
  label: string
  isActive: boolean
  isLoading: boolean
  activeClasses: string
  onClick: () => void
  disabled: boolean
  className?: string
  showActiveReason?: boolean
  activeReason?: string
}

function ToggleButton({
  label,
  isActive,
  isLoading,
  activeClasses,
  onClick,
  disabled,
  className,
  showActiveReason,
  activeReason,
}: ToggleButtonProps) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={isActive}
      role="radio"
      aria-checked={isActive}
      className={cn(
        'h-auto min-h-12 tablet:min-h-[3.5rem] rounded-none px-2 py-1.5 text-xs sm:text-sm whitespace-normal flex flex-col items-center justify-center gap-0.5',
        isActive
          ? cn(activeClasses, 'font-semibold shadow-inner')
          : 'bg-card hover:bg-accent text-muted-foreground hover:text-foreground font-medium',
        className
      )}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <span className="leading-tight">{label}</span>
      )}
      {showActiveReason && activeReason && (
        <span className="text-[10px] opacity-90 leading-tight">{activeReason}</span>
      )}
    </Button>
  )
}
